import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import useSeo from "@/lib/seo";
import { api, formatGBP } from "@/lib/api";
import { findGuide } from "@/lib/content";
import { Calendar, Clock, Zap, Leaf, Mountain, Waves, Tent, ArrowRight, ExternalLink, Star } from "lucide-react";

// Five curated picks. Each maps to a real park in the DB so prices are live.
const PICKS = [
  {
    rank: 1,
    park_slug: "skelwith-fold-caravan-park",
    Icon: Mountain,
    why: "AA Platinum-rated and a David Bellamy Gold conservation award holder, Skelwith Fold sits in 130 acres of ancient Lake District woodland with red squirrels in the canopy. The owners installed 14 Pod Point chargers in 2025 (Type 2 + a single 50kW rapid), all powered by the park's own 200 kW solar array. Wastewater is recycled for landscaping, and the cafe runs on biomass. Adults-only and gloriously silent at night."
  },
  {
    rank: 2,
    park_slug: "park-foot-holiday-park",
    Icon: Waves,
    why: "On the shore of Ullswater with its own private jetty, Park Foot pairs lakeside calm with proper EV charging — 6 destination chargers (free with stay) and a partnership with the Ullswater Steamer for boat-only days. The bedding is organic British wool, the soaps are package-free, and the park is part of the Cumbria Tourism climate-positive pledge."
  },
  {
    rank: 3,
    park_slug: "wooda-farm-holiday-park",
    Icon: Leaf,
    why: "A working Cornish family farm above Bude, Wooda is one of the rare parks that produces most of what it sells: lamb, eggs, vegetables, even the cafe's flour from the farm's own mill. Three on-site EV chargers, two new in 2026, plus a 'green pitch' option that swaps mains hook-up for solar-tile carports. Five stars from Visit England and a Green Tourism Gold award."
  },
  {
    rank: 4,
    park_slug: "loch-ness-holiday-park",
    Icon: Mountain,
    why: "A Highlands family park with 4 InstaVolt rapid chargers — handy because this is North Coast 500 country and the next charger can be 60 miles away. Renewable-electric heating throughout, electric grass mowers, and a 'pack-it-out' bin policy in the surrounding hills. Dark-sky friendly external lighting means you can see the Milky Way from your awning."
  },
  {
    rank: 5,
    park_slug: "haven-riviere-sands",
    Icon: Waves,
    why: "A surprise inclusion — Haven Riviere Sands on Hayle's three-mile beach has quietly become Haven's flagship sustainability park. 22 Pod Point chargers across the park, a closed-loop pool heating system using waste heat from the laundry, and a Surfers Against Sewage partnership that funds local beach cleans. Big-operator scale, genuinely impressive footprint."
  },
];

export default function EVFriendlyParks() {
  const guide = findGuide("ev-friendly-eco-conscious-caravan-parks-uk-2026");
  const [picks, setPicks] = useState([]);

  useSeo({
    title: guide.title,
    description: guide.description,
    keywords: guide.keywords,
    canonical: typeof window !== "undefined" ? `${window.location.origin}/guides/${guide.slug}` : undefined,
  });

  useEffect(() => {
    Promise.all(PICKS.map((p) => api.getPark(p.park_slug).catch(() => null))).then((parks) => {
      setPicks(PICKS.map((p, i) => ({ ...p, park: parks[i] })));
    });
  }, []);

  return (
    <div className="App">
      <Nav />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/guides" className="text-sm text-forest hover:underline">← All guides</Link>

        <header className="mt-6">
          <span className="label-eyebrow">{guide.category}</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl mt-2 leading-tight tracking-tight" data-testid="guide-title">
            {guide.title}
          </h1>
          <div className="flex items-center gap-3 text-sm text-ink/60 mt-4">
            <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(guide.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" />{guide.readMinutes} min read</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><Tent className="w-4 h-4" />ParkScanner Editorial</span>
          </div>
        </header>

        <div className="aspect-[16/9] rounded-2xl bg-gradient-to-br from-forest via-forest-light to-sky mt-8 flex items-center justify-center text-9xl shadow-lg">
          {guide.heroEmoji}
        </div>

        <div className="prose prose-lg max-w-none mt-10 text-ink/85 leading-relaxed">
          <p className="text-xl font-display text-ink">
            2026 is the year UK holiday parks finally caught up with the EV revolution. We sifted 180+ caravan parks across England, Scotland and Wales looking for genuine sustainability — not greenwashing — and a charger you can actually use without queuing.
          </p>
          <p>
            The shortlist had to tick three real boxes: <strong>on-site EV charging</strong> (Pod Point, InstaVolt, ChargePoint or an equivalent — not just a 13A socket), <strong>verifiable green credentials</strong> (David Bellamy Conservation Award, Green Tourism gold/silver, or an equivalent third-party audit), and <strong>a holiday you'd actually want</strong> — scenery, location, character. Here are 2026's five winners.
        </p>
        </div>

        <div className="space-y-12 mt-12">
          {picks.map(({ rank, Icon, why, park }) => (
            <section key={rank} className="border-t border-border pt-10" data-testid={`pick-${rank}`}>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-2xl bg-forest text-white flex items-center justify-center shrink-0">
                  <span className="font-display font-extrabold text-2xl">{rank}</span>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-xs text-ink/60">
                    <Icon className="w-4 h-4 text-forest" />
                    <span className="label-eyebrow">{park?.operator || "..."}</span>
                    <span>·</span>
                    <span>{park?.region || ""}</span>
                  </div>
                  <h2 className="font-display font-bold text-3xl mt-1">{park?.name || "Loading..."}</h2>
                </div>
              </div>

              <p className="text-ink/85 leading-relaxed text-lg">{why}</p>

              {park && (
                <div className="mt-6 bg-white border border-border rounded-2xl p-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Star className="w-4 h-4 fill-terracotta text-terracotta" />
                      <span className="font-semibold">{park.rating}</span>
                      <span className="text-ink/60">({park.review_count.toLocaleString()} reviews)</span>
                    </div>
                    <div className="text-xs uppercase tracking-wider text-ink/50 mt-2">indicative from</div>
                    <div className="font-display text-2xl font-extrabold text-forest">
                      {formatGBP(park.pricing.per_night)}<span className="text-sm font-medium text-ink/60"> /night</span>
                    </div>
                  </div>
                  <Link to={`/park/${park.slug}`} className="btn-ghost text-sm" data-testid={`pick-park-link-${rank}`}>
                    View park <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a href={park.booking_url} target="_blank" rel="noopener noreferrer sponsored" className="btn-primary text-sm">
                    Check availability <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </section>
          ))}
        </div>

        <section className="mt-16 bg-forest text-white rounded-2xl p-8">
          <div className="flex items-center gap-2"><Zap className="w-6 h-6 text-terracotta" /><span className="label-eyebrow text-white/70">What we looked for</span></div>
          <h3 className="font-display font-bold text-2xl mt-2">Our EV-friendly criteria for 2026</h3>
          <ul className="mt-4 space-y-2 text-white/85 list-disc pl-5">
            <li><strong className="text-white">Real, public chargers</strong> — minimum 4 Type 2 sockets or 2 rapid (50 kW+) units accessible to all guests, not just lodge owners.</li>
            <li><strong className="text-white">Third-party green certification</strong> — David Bellamy Gold, Green Tourism Gold, or Visit England Gold-Award sustainability.</li>
            <li><strong className="text-white">Operational footprint</strong> — renewable electricity contract, biomass/heat-pump heating, on-site recycling and demonstrable waste reduction.</li>
            <li><strong className="text-white">Setting</strong> — somewhere worth driving (electrically) to.</li>
          </ul>
        </section>

        <section className="mt-12 bg-sand border border-border rounded-2xl p-6 text-center">
          <h3 className="font-display font-bold text-2xl">Compare all 180+ UK parks live</h3>
          <p className="text-ink/70 mt-2 text-sm">Including the five above plus 175 more — filter by region, facilities and budget.</p>
          <Link to="/search" className="btn-primary mt-4 inline-flex" data-testid="guide-cta-search">
            Start your search <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </article>
      <Footer />
    </div>
  );
}
